import {Authenticator as AmplifyAuthenticator} from '@aws-amplify/ui-react';
import {AuthProvider} from '@saltus/auth';
import {createRoot} from 'react-dom/client';
import {ApolloClient, ApolloProvider, from, useLazyQuery} from '@apollo/client';
import * as Sentry from '@sentry/react';
import {Provider} from 'react-redux';

import {SentryLink} from 'apollo-link-sentry';
import {RetryLink} from '@apollo/client/link/retry';
import {LicenseInfo} from '@mui/x-license';
import {BrowserRouter as Router} from 'react-router-dom';
import React from 'react';
import App from './App';
import DebugProvider from './contexts/DebugProvider';
import {authLink, errorLink, httpLink, authorizationCheckLink} from './apollo';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import {cleanForCuroLink} from './apolloMiddleware';
import './utils/snackbar';
import store from './services/store';
import {apolloCache} from './apolloCache';
import {getEnvironment} from './utils/envTest';
import amplifyInit from './services/amplifyInit';
import {GET_LOGIN_INFO_QUERY} from './hooks/useDjango';
import {getSentryEnvironment} from './getSentryEnvironment';
import SaltusThemeProvider from './contexts/ThemeProvider';

const retryLink = new RetryLink({
  delay: {
    initial: 1000,
    max: 3000,
    jitter: true,
  },
  attempts: {
    max: 2,
    retryIf: (error, operation) => {
      if (error) {
        return false;
      }
      if (operation.getContext().headers?.authorization) {
        // Retry only if the operation has an authorization header
        return true;
      }
      return false;
    },
  },
});

const client = new ApolloClient({
  link: from([
    new SentryLink({
      setTransaction: true,
      setFingerprint: true,
      attachBreadcrumbs: {
        includeQuery: true,
        includeError: true,
        includeVariables: true,
        includeContextKeys: ['headers'],

        includeFetchResult: true,
      },
    }),
    retryLink,
    cleanForCuroLink,
    errorLink,
    authLink,
    authorizationCheckLink,
    httpLink,
  ]),
  cache: apolloCache,
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
    },
    mutate: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  },
  assumeImmutableResults: true,
});

const backendURLs = [
  'http://django:8000',
  process.env.VITE_APP_BACKEND_URL,
  process.env.VITE_APP_MICROSERVICES_URL,
  'https://oneview.saltusdev.co.uk',
  'https://api.test.oneview.saltusdev.co.uk',
  'https://api.uat.oneview.saltusdev.co.uk',
  window.location.origin,
];

const enableDevSentry = true;

Sentry.init({
  dsn: 'https://4a9177b8b048458680d56e4d00b95b9f@o1418780.ingest.sentry.io/6765336',
  integrations: [
    Sentry.browserTracingIntegration({traceXHR: true, traceFetch: true}),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      networkDetailAllowUrls: backendURLs,
      networkCaptureBodies: true,
      networkRequestHeaders: ['frontendurl', 'sentry-trace'],
    }),
    Sentry.graphqlClientIntegration({
      endpoints: [/\/graphql$/],
    }),
  ],
  replaysSessionSampleRate: getEnvironment() === 'DEV' ? (enableDevSentry ? 1 : 0) : 1,
  replaysOnErrorSampleRate: getEnvironment() === 'DEV' ? (enableDevSentry ? 1 : 0) : 1,
  maskAllText: false,
  maskAllInputs: false,
  blockAllMedia: false,
  networkDetailAllowUrls: backendURLs,
  tracePropagationTargets: backendURLs,
  networkCaptureBodies: true,
  environment: getSentryEnvironment(),
  // Set tracesSampleRate to 1.0 to capture 100%
  // of initials for performance monitoring.
  // We recommend adjusting this value in production
  profilesSampleRate: process.env.VITE_APP_USING_RUNNER
    ? 1
    : process.env.SENTRY_PROFILE_SAMPLE_RATE,
  tracesSampleRate: process.env.VITE_APP_USING_RUNNER
    ? 1
    : process.env.SENTRY_TRACE_SAMPLE_RATE,
  ignoreErrors: [
    // ignores material ui
    'Material-UI: You have provided an out-of-range value',
    // This custom event was created to display errors to end users
    'CustomEvent: Event `CustomEvent` (type=error) captured as exception',
    // this is caused when the user leaves the dashboard running for an amount of time.
    // If the user refreshes the page, they are logged back in
    'User is not authenticated',
    // already handled by graphql, in sentry that's just noise
    'Network error',
  ],
  denyUrls: process.env.VITE_APP_USING_RUNNER
    ? []
    : [
        // Don't track localhost as we're expected to make mistakes as we develop the dashboard
        /127\.0\.0\.1:3000\//i,
        /localhost:3000\//i,
      ],
  maxBreadcrumbs: getEnvironment() === 'DEV' ? (enableDevSentry ? 10 : 0) : 30, // used for smaller payload size
  beforeBreadcrumb: (breadcrumb, hint) => {
    // if (breadcrumb.level === 'warning') {
    //   return null;
    // }
    if (
      breadcrumb.message?.startsWith(
        'Material-UI: A component is changing the uncontrolled value state'
      )
    ) {
      return null;
    }
    if (breadcrumb.message?.startsWith('Warning: Failed %s type')) {
      return null;
    }
    return breadcrumb;
    // return excludeGraphQLFetch(breadcrumb);
    // useful reference from sentry:
    // // We ignore our own logger and rest of the buttons just for presentation purposes
    // if (breadcrumb.message?.startsWith('Sentry Logger')) return null;
    // if (breadcrumb.category !== 'ui.click' || hint.event.target.id !== 'breadcrumb-hint') return null;

    // // If we have a `ui.click` type of breadcrumb, eg. clicking on a button we defined in index.html
    // // We will extract a `data-label` attribute from it and use it as a part of the message
    // if (breadcrumb.category === 'ui.click') {
    //   const label = hint.event.target.dataset.label;
    //   if (label) {
    //     breadcrumb.message? = `User clicked on a button with label "${label}"`;
    //   }
    // }
    // return breadcrumb;
  },
});

const container = document.getElementById('root');
const root = createRoot(container); // createRoot(container!) if you use TypeScript

/**
 * Yes, the license is designed to be public... go figure!
 * https://mui.com/x/introduction/licensing/#license-key-security
 * You need to call setLicenseKey() before React renders the first component.
 */
LicenseInfo.setLicenseKey(
  'a937f5b91202553ac60749104e309b55Tz0xMzE1OTcsRT0xODE3MDc4Mzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1pbml0aWFsLEtWPTI='
);

function WrappedApp() {
  const [getAdviserEmail] = useLazyQuery(GET_LOGIN_INFO_QUERY);

  // @saltus/auth configuration
  const forgotPassword = async (email) => {
    // Code to execute when forgot password form is submit
  };

  const getUserData = async (user) => {
    // Code to execute to retrieve additional user data from an external source
    if (user && user.attributes) {
      const {data} = await getAdviserEmail({
        variables: {
          adviserId: user.userId,
        },
      });

      return {
        mobile: data.adviserMobileNumber,
      };
    }

    return {};
  };

  const passwordSet = async () => {
    // Code to execute when a new password is set
  };

  const signOut = async () => {
    // Code to execute when a user signs out
  };

  const {config, cookieStorage} = amplifyInit();

  return (
    <AmplifyAuthenticator.Provider>
      <AuthProvider
        config={config}
        keyValueStorage={cookieStorage}
        onForgotPassword={forgotPassword}
        onGetUserData={getUserData}
        onPasswordSet={passwordSet}
        onSignOut={signOut}
      >
        <DebugProvider>
          <Provider store={store}>
            <SaltusThemeProvider>
              <Router>
                <App />
              </Router>
            </SaltusThemeProvider>
          </Provider>
        </DebugProvider>
      </AuthProvider>
    </AmplifyAuthenticator.Provider>
  );
}

root.render(
  <ErrorBoundary>
    <ApolloProvider client={client}>
      <WrappedApp />
    </ApolloProvider>
  </ErrorBoundary>
);
